import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, concatMap, EMPTY, Observable, switchMap } from 'rxjs';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/shared/state-store';
import { BnToastrService } from '@bn/web-ui/toastr';
import { BotCategorizationAction, BotCategorizationGroup, BotCategorizationService, BotCategory, BotCategoryAction } from '../openapi';

const events = getStoreEvents('SHIELD_BOT_CATEGORIZATION', ['LIST', 'SET_BOT', 'SET_CATEGORY']);

export interface ShieldBotCategorizationStoreType {
  categories: BotCategorizationGroup[];
}

const initialState: ShieldBotCategorizationStoreType = {
  categories: [],
};

type BotCategorizationMutation =
  | { type: 'bot'; shieldZoneId: number; botId: number; action: BotCategorizationAction }
  | { type: 'category'; shieldZoneId: number; category: BotCategory; action: BotCategoryAction };

const applyBotAction = (categories: BotCategorizationGroup[], botId: number, action: BotCategorizationAction): BotCategorizationGroup[] =>
  categories.map((group) => ({
    ...group,
    bots: (group.bots ?? []).map((bot) => (bot.botId === botId ? { ...bot, action } : bot)),
  }));

export const ShieldBotCategorizationApiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield.BotCategorization'),
  withState<ShieldBotCategorizationStoreType>(initialState),
  withDefaultRequestStatusState(),

  withMethods(
    (store, botCategorizationService = inject(BotCategorizationService), toastrService = inject(BnToastrService), destroyRef = inject(DestroyRef)) => {
      const botUpdate$ = (shieldZoneId: number, botId: number, action: BotCategorizationAction): Observable<unknown> => {
        updateState(store, events.SET_BOT.REQUEST, store.setLoading(StoreRequest.Update, [`bot-${botId}`]));

        return botCategorizationService.setBotCategorizationAction({ shieldZoneId, botId, updateBotCategorizationConfigurationRequest: { action } }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              if (response.error) {
                const message = response.error.message ?? 'Failed to update bot action.';

                toastrService.danger(message);
                updateState(store, events.SET_BOT.ERROR, store.setErrorMessage(StoreRequest.Update, message, [`bot-${botId}`]));

                return;
              }

              updateState(
                store,
                events.SET_BOT.SUCCESS,
                { categories: applyBotAction(store.categories(), botId, action) },
                store.setFinished(StoreRequest.Update, [`bot-${botId}`]),
              );
            },
            error: ({ error }) => updateState(store, events.SET_BOT.ERROR, store.setError(StoreRequest.Update, error, [`bot-${botId}`])),
          }),
        );
      };

      const categoryUpdate$ = (shieldZoneId: number, category: BotCategory, action: BotCategoryAction): Observable<unknown> => {
        updateState(store, events.SET_CATEGORY.REQUEST, store.setLoading(StoreRequest.Update, [`category-${category}`]));

        return botCategorizationService.setBotCategoryAction({ shieldZoneId, category, updateBotCategoryConfigurationRequest: { action } }).pipe(
          takeUntilDestroyed(destroyRef),
          switchMap((response) => {
            if (response.error) {
              const message = response.error.message ?? 'Failed to update category action.';

              toastrService.danger(message);
              updateState(store, events.SET_CATEGORY.ERROR, store.setErrorMessage(StoreRequest.Update, message, [`category-${category}`]));

              return EMPTY;
            }

            return botCategorizationService.listBotCategorizations({ shieldZoneId }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (listResponse) => {
                  if (listResponse.error) {
                    const message = listResponse.error.message ?? 'Failed to reload bot controls.';

                    toastrService.danger(message);
                    updateState(store, events.SET_CATEGORY.ERROR, store.setErrorMessage(StoreRequest.Update, message, [`category-${category}`]));

                    return;
                  }

                  updateState(
                    store,
                    events.SET_CATEGORY.SUCCESS,
                    { categories: listResponse.categories ?? [] },
                    store.setFinished(StoreRequest.Update, [`category-${category}`]),
                  );
                },
                error: ({ error }) => updateState(store, events.SET_CATEGORY.ERROR, store.setError(StoreRequest.Update, error, [`category-${category}`])),
              }),
            );
          }),
          catchError((response: HttpErrorResponse) => {
            updateState(store, events.SET_CATEGORY.ERROR, store.setError(StoreRequest.Update, response.error, [`category-${category}`]));

            return EMPTY;
          }),
        );
      };

      const mutate = rxMethod<BotCategorizationMutation>(
        concatMap((mutation) =>
          mutation.type === 'bot'
            ? botUpdate$(mutation.shieldZoneId, mutation.botId, mutation.action)
            : categoryUpdate$(mutation.shieldZoneId, mutation.category, mutation.action),
        ),
      );

      return {
        getBotCategorizations: rxMethod<{ shieldZoneId: number }>(
          switchMap(({ shieldZoneId }) => {
            updateState(store, events.LIST.REQUEST, { categories: [] }, store.setLoading(StoreRequest.List));

            return botCategorizationService.listBotCategorizations({ shieldZoneId }).pipe(
              takeUntilDestroyed(destroyRef),
              tapResponse({
                next: (response) => {
                  if (response.error) {
                    const message = response.error.message ?? 'Failed to load bot controls.';

                    toastrService.danger(message);
                    updateState(store, events.LIST.ERROR, store.setErrorMessage(StoreRequest.List, message));

                    return;
                  }

                  updateState(store, events.LIST.SUCCESS, { categories: response.categories ?? [] }, store.setFinished(StoreRequest.List));
                },
                error: ({ error }) => updateState(store, events.LIST.ERROR, store.setError(StoreRequest.List, error)),
              }),
            );
          }),
        ),

        setBotAction(params: { shieldZoneId: number; botId: number; action: BotCategorizationAction }): void {
          mutate({ type: 'bot', ...params });
        },

        setCategoryAction(params: { shieldZoneId: number; category: BotCategory; action: BotCategoryAction }): void {
          mutate({ type: 'category', ...params });
        },

        clearCategoryOverrides({ shieldZoneId, category }: { shieldZoneId: number; category: BotCategory }): void {
          const group = store.categories().find((item) => item.category === category);
          const overridden = (group?.bots ?? []).filter((bot) => bot.action !== BotCategorizationAction.None);

          overridden.forEach((bot) => mutate({ type: 'bot', shieldZoneId, botId: bot.botId, action: BotCategorizationAction.None }));
        },
      };
    },
  ),
);
